import { OrderedMap } from 'immutable';
import { AppState, AppStateActionParams } from 'records/AppState';
import { Sheet, SheetId } from 'records/Sheet';
import { SheetVersion } from 'records/SheetVersion';
import { getSheets, updateSheetVersionInfo } from 'services/sheetService';
import { getSheetSettings } from 'services/sheetSettingsService';
import { UpdateSheet } from '../records/UpdateSheet';

export interface SheetActions {
  getSheets: (actionParams: AppStateActionParams) => Promise<AppState>;

  getSheetSettings: (actionParams: AppStateActionParams) => Promise<AppState>;

  setSelectedSheet: (
    actionParams: AppStateActionParams,
    selectedSheetId: string
  ) => AppState;

  updateSheetVersionInfo: (
    actionParams: AppStateActionParams,
    updatedSelectedSheets: UpdateSheet
  ) => Promise<AppState>;
}

const sortByPublishedTimestamp = (sheet: Sheet) => {
  return sheet.update('versions', (versions) =>
    versions.sortBy((v) => v.pubishedtimestamp)
  );
};

/**
 *
 * @param accumulated
 * @param currentSheet
 * Merge all sheet versions into one entry with complete versions list
 * Convert to OrderedMap and restructure it to OrderedMap<PageId, Sheet>
 * @returns OrderedMap<PageId, Sheet>
 */
const restructureAndMergeVersions = (
  accumulated: OrderedMap<string, Sheet>,
  currentSheet: Sheet
): OrderedMap<SheetId, Sheet> => {
  // Меrge all versions of sheet into single entry
  const sheetInAccumulated = accumulated.get(currentSheet.sheetid);
  let updatedSheet = currentSheet;
  if (sheetInAccumulated) {
    const updatedVersions = currentSheet.versions.push(
      ...sheetInAccumulated.versions
    );
    updatedSheet = currentSheet.set('versions', updatedVersions);
  }
  // Create OrderedMap<PageId, Sheet>.
  return accumulated.set(currentSheet.sheetid, updatedSheet);
};

/**
 *
 * @param sheet
 * Sort by sheetnumber (found in first sheetversion)
 */
const sortBySheetNumber = (sheet: Sheet) => {
  return sheet.versions.get(0, new SheetVersion()).sheetnumber;
};

const sheetActions: SheetActions = {
  getSheets: async ({ state }: AppStateActionParams): Promise<AppState> => {
    const { selectedProject } = state();

    const response = await getSheets(selectedProject);
    const sheetsMap = response
      // Sort sheet versions.
      .map(sortByPublishedTimestamp)
      // Create OrderedMap<PageId, Sheet>.
      .reduce(restructureAndMergeVersions, OrderedMap<SheetId, Sheet>())
      // Sort by sheetnumber (found in first sheetversion)
      .sortBy(sortBySheetNumber);

    return state().set('sheets', sheetsMap);
  },

  getSheetSettings: async ({
    state,
  }: AppStateActionParams): Promise<AppState> => {
    const { selectedProject } = state();
    const response = await getSheetSettings(selectedProject);

    return state().setIn(['settings', 'sheets'], response);
  },

  setSelectedSheet: (
    { state }: AppStateActionParams,
    selectedSheetId: string
  ): AppState => {
    return state().set('selectedSheet', selectedSheetId);
  },

  updateSheetVersionInfo: async (
    { state }: AppStateActionParams,
    updatedSheet: UpdateSheet
  ): Promise<AppState> => {
    await updateSheetVersionInfo(updatedSheet);

    return state();
  },
};

export default sheetActions;
